import { IconShieldPlus, IconShieldMinus, IconUserMinus, IconSearch } from "@tabler/icons-react";
import AppDialog from "components/common/AppDialog";
import { AdvancedConfirmDialog, NoData, Pagination, StyledTableHead } from "components";
import { LangConstant, SystemConstant } from "const";
import { useChannelMembers, useRemoveChannelMember, useUpdateChannelMemberRole } from "hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AddChannelMemberDialog from "./AddChannelMemberDialog";

const VIEW_MODE = {
  addMember: "addMember",
  deleteMember: "deleteMember",
  changeAdminRole: "changeAdminRole",
};

const UpdateChannelDialog = ({ open, channel, onClose, zIndex }) => {
  const { t: getLabel } = useTranslation();
  const labels = getLabel(LangConstant.OBJ_UPDATE_CHANNEL_DIALOG, { returnObjects: true });
  const isChannel = channel?.groupType !== 1;

  const dialogTitle = isChannel
    ? `Thông tin kênh: ${channel?.groupName || ""}`
    : `Thông tin nhóm: ${channel?.groupName || ""}`;

  const { allMembers, totalMembers, totalAdmins, handleSearch, handleRefreshQuery } = useChannelMembers({
    groupId: channel?.id,
  });
  const { removeChannelMember } = useRemoveChannelMember();
  const { grantAdmin, revokeAdmin } = useUpdateChannelMemberRole();

  const [selectedMember, setSelectedMember] = useState(null);
  const [viewMode, setViewMode] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = 10;
  const totalPages = Math.ceil(allMembers.length / pageSize);
  const members = allMembers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageChange = (e, page) => {
    setCurrentPage(page);
  };

  const columns = [
    { label: labels.colFullName, align: "left" },
    { label: labels.colUnit, align: "left", width: "15%" },
    { label: labels.colDepartment, align: "left", width: "15%" },
    { label: labels.colPosition, align: "left", width: "15%" },
    { label: labels.colRole, align: "left", width: "15%" },
    { label: labels.colAction, align: "center", width: "12%" },
  ];

  const handleSearchMember = event => {
    handleSearch(event.target.value);
    setCurrentPage(1);
  };

  const handleRemoveMember = member => () => {
    setSelectedMember(member);
    setViewMode(VIEW_MODE.deleteMember);
  };

  const handleChangeAdminRole = member => () => {
    setSelectedMember(member);
    setViewMode(VIEW_MODE.changeAdminRole);
  };

  const handleAddMember = () => {
    setViewMode(VIEW_MODE.addMember);
  };

  const handleReset = () => {
    setViewMode(null);
    setSelectedMember(null);
  };

  const handleConfirmRemoveMember = () => {
    removeChannelMember({
      groupId: channel?.id,
      memberId: selectedMember.id,
      onResetMembers: handleRefreshQuery,
      onSuccess: handleReset,
    });
  };

  const handleConfirmChangeAdminRole = () => {
    const action = selectedMember.isAdmin ? revokeAdmin : grantAdmin;
    action({
      groupId: channel?.id,
      memberId: selectedMember.id,
      onResetMembers: handleRefreshQuery,
      onSuccess: handleReset,
    });
  };

  return (
    <AppDialog
      open={open}
      title={dialogTitle}
      onClose={onClose}
      style={{ width: 1200, maxWidth: "95vw" }}
      zIndex={zIndex || 1055}
    >
      <p style={{ fontSize: 16, fontWeight: 700, margin: "8px 0" }}>
        {labels.totalMembers}: <span style={{ fontWeight: 400 }}>{totalMembers}</span>
      </p>

      <div className="input-group mb-3">
        <input
          type="text"
          className="form-control"
          placeholder={labels.searchPlaceholder}
          onChange={handleSearchMember}
        />
        <span className="input-group-text">
          <IconSearch size={16} />
        </span>
      </div>

      <div className="d-flex align-items-center justify-content-between mb-2">
        <strong style={{ fontSize: 15 }}>{labels.userListTitle}</strong>
        <button className="btn btn-primary" onClick={handleAddMember}>
          {labels.addMember}
        </button>
      </div>

      <div>
        <table className="table table-vcenter table-hover">
          <StyledTableHead columns={columns} />
          <tbody>
            {members.map(member => {
              const isDisableRoleBtn = member.isAdmin && totalAdmins <= 1;
              const isDisableDeleteBtn = totalMembers <= SystemConstant.MIN_CHANNEL_MEMBER_LENGTH || isDisableRoleBtn;
              return (
                <tr
                  key={member.id}
                  style={{ backgroundColor: member.isAdmin ? "rgba(32, 107, 196, 0.12)" : "inherit" }}
                >
                  <td className="text-left">{member.name}</td>
                  <td className="text-left">{member.unitName || "--"}</td>
                  <td className="text-left">{member.departmentName || "--"}</td>
                  <td className="text-left">{member.positionName || "--"}</td>
                  <td className="text-left">{member.isAdmin ? (isChannel ? labels.roleAdmin : (getLabel("TXT_ROLE_ADMIN") || "Trưởng nhóm")) : (isChannel ? labels.roleMember : (getLabel("TXT_ROLE_MEMBER") || "Thành viên"))}</td>
                  <td className="text-center">
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      <button
                        className="btn btn-icon btn-ghost-info"
                        style={{ width: 32, height: 32 }}
                        disabled={isDisableRoleBtn}
                        title={
                          isDisableRoleBtn
                            ? (isChannel
                                ? getLabel("TXT_REVOKE_CHANNEL_ADMIN_DISABLED")
                                : getLabel("TXT_REVOKE_GROUP_ADMIN_DISABLED"))
                            : member.isAdmin
                            ? (isChannel
                                ? getLabel("TXT_REVOKE_CHANNEL_ADMIN_BTN")
                                : getLabel("TXT_REVOKE_GROUP_ADMIN_BTN"))
                            : (isChannel
                                ? getLabel("TXT_GRANT_CHANNEL_ADMIN_BTN")
                                : getLabel("TXT_GRANT_GROUP_ADMIN_BTN"))
                        }
                        onClick={handleChangeAdminRole(member)}
                      >
                        {member.isAdmin ? <IconShieldMinus size={18} /> : <IconShieldPlus size={18} />}
                      </button>

                      <button
                        className="btn btn-icon btn-ghost-danger"
                        style={{ width: 32, height: 32 }}
                        disabled={isDisableDeleteBtn}
                        title={
                          isDisableDeleteBtn
                            ? member.isAdmin && totalAdmins <= 1
                              ? (isChannel
                                  ? getLabel("TXT_REVOKE_CHANNEL_ADMIN_DISABLED")
                                  : getLabel("TXT_REVOKE_GROUP_ADMIN_DISABLED"))
                              : (isChannel
                                  ? getLabel("TXT_REMOVE_CHANNEL_MEMBER_DISABLED_MIN")
                                  : getLabel("TXT_REMOVE_GROUP_MEMBER_DISABLED_MIN"))
                            : (isChannel
                                ? getLabel("TXT_REMOVE_CHANNEL_MEMBER_BTN")
                                : getLabel("TXT_REMOVE_GROUP_MEMBER_BTN"))
                        }
                        onClick={handleRemoveMember(member)}
                      >
                        <IconUserMinus size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {members.length === 0 && <NoData />}
      </div>

      {totalPages > 1 && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
      )}

      {viewMode === VIEW_MODE.changeAdminRole && selectedMember && (
        <AdvancedConfirmDialog
          open
          zIndex={zIndex ? zIndex + 15 : 1070}
          title={getLabel(selectedMember.isAdmin
            ? (isChannel ? "TXT_REVOKE_CHANNEL_ADMIN_TITLE" : "TXT_REVOKE_GROUP_ADMIN_TITLE")
            : (isChannel ? "TXT_ADD_CHANNEL_ADMIN_TITLE" : "TXT_ADD_GROUP_ADMIN_TITLE")
          )}
          content={getLabel(selectedMember.isAdmin
            ? (isChannel ? "TXT_REVOKE_CHANNEL_ADMIN_CONTENT" : "TXT_REVOKE_GROUP_ADMIN_CONTENT")
            : (isChannel ? "TXT_ADD_CHANNEL_ADMIN_CONTENT" : "TXT_ADD_GROUP_ADMIN_CONTENT")
          )}
          submitProps={{ submitText: getLabel("L_CONFIRM_BUTTON"), onClick: handleConfirmChangeAdminRole }}
          cancelProps={{ onClick: handleReset }}
        />
      )}

      {viewMode === VIEW_MODE.deleteMember && selectedMember && (
        <AdvancedConfirmDialog
          open
          zIndex={zIndex ? zIndex + 15 : 1070}
          title={getLabel(isChannel ? "TXT_DELETE_CHANNEL_MEMBER_TITLE" : "TXT_DELETE_GROUP_MEMBER_TITLE")}
          content={getLabel(isChannel ? "TXT_DELETE_CHANNEL_MEMBER_CONTENT" : "TXT_DELETE_GROUP_MEMBER_CONTENT")}
          submitProps={{ submitText: getLabel("L_CONFIRM_BUTTON"), onClick: handleConfirmRemoveMember }}
          cancelProps={{ onClick: handleReset }}
        />
      )}

      {viewMode === VIEW_MODE.addMember && (
        <AddChannelMemberDialog
          open
          groupId={channel?.id}
          onClose={handleReset}
          onRefreshMemberList={handleRefreshQuery}
          groupName={channel?.groupName}
          groupType={channel?.groupType}
          zIndex={zIndex ? zIndex + 10 : 1065}
        />
      )}
    </AppDialog>
  );
};

export default UpdateChannelDialog;
